<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ApiError, api } from '../api';
import { safeRedirect } from '../navigation';
import { useAuthStore } from '../stores/auth';

const auth = useAuthStore();
const route = useRoute();
const router = useRouter();

const LOGIN_ERRORS: Record<string, string> = {
  NOT_INVITED: "Ce compte n'a pas été invité. Demandez une invitation à l'administrateur.",
  USER_DISABLED: 'Ce compte est désactivé.',
  DOMAIN_NOT_ALLOWED: 'Seuls les comptes Google @evolusite.fr peuvent se connecter.',
  GOOGLE_NOT_CONFIGURED: "La connexion Google n'est pas encore configurée sur ce serveur.",
  GOOGLE_ACCOUNT_MISMATCH: 'Ce compte Google ne correspond pas au membre enregistré.',
  OAUTH_CANCELLED: 'Connexion annulée.',
  OAUTH_STATE_MISMATCH: 'La connexion a expiré, merci de recommencer.',
  OAUTH_FAILED: 'La connexion Google a échoué, merci de recommencer.',
};

const queryError = computed(() => {
  const code = route.query.error;
  return typeof code === 'string' ? (LOGIN_ERRORS[code] ?? 'La connexion a échoué.') : '';
});

const email = ref('');
const devError = ref('');
const submitting = ref(false);

async function devLogin(): Promise<void> {
  submitting.value = true;
  devError.value = '';
  try {
    await auth.devLogin(email.value);
    await router.replace(safeRedirect(route.query.redirect));
  } catch (err) {
    devError.value = err instanceof ApiError ? err.message : 'Connexion impossible.';
  } finally {
    submitting.value = false;
  }
}
</script>

<template>
  <div class="login">
    <div class="login-card">
      <h2>Cockpit Evolusite</h2>
      <p>Accès réservé aux comptes Google @evolusite.fr invités par l'administrateur.</p>
      <p v-if="queryError" class="login-error" role="alert">{{ queryError }}</p>
      <a class="btn primary" style="padding: 10px" :href="api.googleStartUrl">Continuer avec Google</a>

      <template v-if="auth.config?.devLoginEnabled">
        <div class="or">ou</div>
        <form class="dev-login" aria-label="Connexion de développement" @submit.prevent="devLogin">
          <label class="field" for="dev-email">Connexion de développement (désactivée en production)</label>
          <div class="row">
            <input
              id="dev-email"
              v-model="email"
              type="email"
              required
              placeholder="mmagne@evolusite.fr"
              autocomplete="email"
            />
            <button class="btn" type="submit" :disabled="submitting">Se connecter</button>
          </div>
          <p v-if="devError" class="form-error" role="alert">{{ devError }}</p>
        </form>
      </template>
    </div>
  </div>
</template>
